import mitt from 'mitt';
import Keycloak from 'keycloak-js';
import { log } from '@/app/shared/utils';
import type { Transport } from './transport';
import type { AuthState, UserInfo } from './types';

interface AuthOptions {
  url: string;
  realm: string;
  clientId: string;
  silentCheckSsoRedirectUri: string;
  logoutRedirectUri: string;
  transport: Transport;
}

export class Auth {
  private transport: Transport;
  private emitter = mitt<{ change: AuthState }>();
  private readonly logoutRedirectUri: string;
  private readonly silentCheckSsoRedirectUri: string;
  private readonly keycloak: Keycloak;
  private state: AuthState = 'unauthenticated';

  constructor(options: AuthOptions) {
    this.transport = options.transport;
    this.logoutRedirectUri = options.logoutRedirectUri;
    this.silentCheckSsoRedirectUri = options.silentCheckSsoRedirectUri;

    this.keycloak = new Keycloak({
      url: options.url,
      realm: options.realm,
      clientId: options.clientId,
    });

    this.keycloak.onTokenExpired = () => this.keycloak.updateToken(0);
    this.keycloak.onAuthSuccess = () => {
      log('keycloak auth success');
      this.setState('authenticated');
    };
    this.keycloak.onAuthLogout = () => {
      this.setState('unauthenticated');
    };
    this.keycloak.onAuthRefreshError = () => {
      log('keycloak auth refresh error');
      this.setState('error');
      this.keycloak.clearToken();
    };
    this.keycloak.onAuthError = () => {
      log('keycloak auth error');
      this.setState('error');
      this.keycloak.clearToken();
    };
  }

  private setState(state: AuthState) {
    this.state = state;
    this.emitter.emit('change', state);
  }

  getState(): AuthState {
    return this.state;
  }

  async init() {
    try {
      await this.keycloak.init({
        checkLoginIframe: false,
        enableLogging: false,
        onLoad: 'check-sso',
        silentCheckSsoRedirectUri: this.silentCheckSsoRedirectUri,
      });
    } catch (error) {
      log('Failed to initialize keycloak');
      this.setState('error');
    }
  }

  async logout(redirectUri?: string) {
    await this.keycloak.logout({
      redirectUri:
        redirectUri ??
        this.keycloak.createLoginUrl({
          redirectUri: this.logoutRedirectUri,
        }),
    });
  }

  async login(redirectUri?: string) {
    await this.keycloak.login({
      prompt: 'login',
      redirectUri,
    });
  }

  async getToken() {
    try {
      await this.keycloak.updateToken(30);
      return this.keycloak.token;
    } catch (error) {
      log('Failed to get keycloak token');
      this.logout();
    }
  }

  isAuthenticated(): boolean {
    return this.state === 'authenticated';
  }

  async getUserInfo(): Promise<UserInfo | undefined> {
    const info = await this.keycloak.loadUserInfo();
    return info as UserInfo;
  }

  async changeUserPassword() {
    return this.keycloak.login({ action: 'UPDATE_PASSWORD' });
  }

  createLoginUrl(redirectUri: string) {
    return this.keycloak.createLoginUrl({
      redirectUri,
    });
  }

  onStateChanged(handler: (state: AuthState) => void) {
    this.emitter.on('change', handler);
    return () => this.emitter.off('change', handler);
  }
}
